import React, { useState, useEffect } from 'react';
import { Banner, Button, Table } from '@ds/ui';
import HSMInfo from './hsmKey/HSMInfo';
import HSMKeys from './hsmKey/HSMKeys';
import NumKeys from './numKey/NumKeys';
import { KeyPair } from './keyPair/KeyPair';
import { KeyPairInfo } from '../types/KeyPairInfo'; 

import ApplianceNameComponent from './applianceInfoTable/ApplianceName';
import ApplianceDetails from './applianceInfoTable/ApplianceDetails';
import StatusUpdateComponent from './applianceInfoTable/StatusSelection';
import { useConfig } from '../context/ConfigContext';
import NamedKeysInternal from './namedKey/NamedKeysInternal';
import NamedKeys from './namedKey/NamedKeys';
import ExportInfo from '../types/ExportInfo';

interface MainPageProps {
  pendingKeys: string;
  namedKeys: string;
  keyPairInfo: KeyPairInfo | null;
  setKeyPairInfo: (keyPairInfo: KeyPairInfo | null) => void;
  onPendingKeysChange: (keys: string) => void;
  onNamedKeysChange: (keys: string) => void;
  onBannerUpdate: (bannerInfo: any) => void;
  onManageKeyPair: () => void;
  onRestoreKeys: () => void;
}

const MainPage: React.FC<MainPageProps> = ({
  pendingKeys,
  namedKeys,
  keyPairInfo,
  setKeyPairInfo,
  onPendingKeysChange,
  onNamedKeysChange,
  onBannerUpdate,
  onManageKeyPair,
  onRestoreKeys
}) => {
  const { userName, applianceId, encryptionVersion, blocksize } = ApplianceDetails();
  const { internalSiteEnabled } = useConfig();
  const [hsmConfiguration, setHSMConfiguration] = useState(true);
  const [namedKeyConfiguration, setNamedKeyConfiguration] = useState(true);
  const [exportInfo, setExportInfo] = useState<ExportInfo>();

  const handleExportInfoUpdate = (newExportInfo: ExportInfo) => {
    setExportInfo(newExportInfo);
  };

  const onHSMConfiguration = (isConfigured: boolean) => {
    setHSMConfiguration(isConfigured);
  };

  const onNamedKeyConfiguration = (isConfigured: boolean) => {
    setNamedKeyConfiguration(isConfigured);
  };

  return (
    <>
      <div key={pendingKeys}>
        {(String(pendingKeys) !== "0" || (internalSiteEnabled && String(namedKeys) !== "0")) ? (
          <Banner kind="subtle" visible>
            Security Appliance - This appliance has 
            {String(pendingKeys) !== "0" && <b> {pendingKeys.toString().toUpperCase()} Numbered Keys</b>}
            {internalSiteEnabled && String(namedKeys) !== "0" && (
              <>{String(pendingKeys) !== "0" ? ' and ' : ' '}<b>{namedKeys.toString().toUpperCase()} Named Keys</b></>
            )}
          </Banner>
        ) : null}
      </div>
      <div style={{ 
        paddingTop: `${pendingKeys === "0" ? '0' : '1.0rem'}`,
        verticalAlign: 'top'
      }}>
      <Table>
        <Table.Row>
          <Table.Cell _style={{ 
            verticalAlign: 'top'
          }}>
            <strong>Hello, {userName}!</strong>
          </Table.Cell>
        </Table.Row>
      </Table>
      </div>
      <div>
      <Table columnSettings={[
        { alignment: 'left', width: '12.5rem' },
        { alignment: 'left' },
        { alignment: 'left' },
        { alignment: 'left' }
      ]} >
        <Table.Body>
          <Table.Row >
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Appliance ID</strong></Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', verticalAlign: 'top', paddingLeft: '2rem' }}>{applianceId}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Appliance Name</strong></Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', verticalAlign: 'top', paddingLeft: '2rem' }}>
              <ApplianceNameComponent />
            </Table.Cell>
          </Table.Row>
          {internalSiteEnabled && (
            <>
              {typeof encryptionVersion === 'string' && encryptionVersion.length > 0 ? (
                <Table.Row>
                  <Table.Cell _style={{ verticalAlign: 'top' }} ><strong>Encryption Version</strong></Table.Cell>
              <Table.Cell _style={{ verticalAlign: 'top', paddingLeft: '2rem' }}>{encryptionVersion}</Table.Cell>
            </Table.Row>
          ) : null}
          {typeof blocksize === 'string' && blocksize.length > 0 ? (
            <Table.Row>
              <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Blocksize</strong></Table.Cell>
              <Table.Cell _style={{ verticalAlign: 'top', paddingLeft: '2rem' }}>{blocksize}</Table.Cell>
            </Table.Row>
              ) : null}
            </>
          )}  
          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Status</strong></Table.Cell>
            <Table.Cell _style={{ verticalAlign: 'top' , paddingLeft: '2rem'}}>
              <StatusUpdateComponent />
            </Table.Cell>
          </Table.Row>


          {hsmConfiguration ? (
            <>
            <Table.Row> 
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>  HSM Info</strong></Table.Cell>
            <Table.Cell _style={{ verticalAlign: 'top' }}>
                <HSMInfo 
                  onBannerUpdate={onBannerUpdate}
                  onHSMConfiguration={onHSMConfiguration}
                />
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>HSM Keys</strong></Table.Cell>
            <Table.Cell _style={{ verticalAlign: 'top' }}>
                <HSMKeys 
                  onBannerUpdate={onBannerUpdate}
                />
            </Table.Cell>
          </Table.Row>
          </>
          ) : (
            <>
            <Table.Row>
              <Table.Cell _style={{ verticalAlign: 'top' }}><strong>HSM Status</strong></Table.Cell>
              <Table.Cell _style={{ verticalAlign: 'top' , paddingLeft: '2rem'}}>
                 {"HSM is not configured"}
              </Table.Cell>
            </Table.Row>
            </>
          )}
          

          {(
            <>
              {namedKeyConfiguration && (
                <Table.Row>
                  <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Named Keys</strong></Table.Cell>
                  <Table.Cell>
                    <NamedKeys 
                      onNamedKeyConfiguration={onNamedKeyConfiguration} 
                      onBannerUpdate={onBannerUpdate}
                      onExportInfoUpdate={handleExportInfoUpdate}
                    />
                  </Table.Cell>
                </Table.Row>
              )}
            </>
          )}

          {internalSiteEnabled && (
            <>
              <Table.Row>
                <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Named Keys</strong></Table.Cell>
                <Table.Cell>
                  <NamedKeysInternal onNamedKeysChange={onNamedKeysChange} onBannerUpdate={onBannerUpdate}/>
                </Table.Cell>
              </Table.Row>
            </>
          )}

          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Numbered Keys</strong></Table.Cell>
            <Table.Cell>
              <NumKeys 
                onPendingKeysChange={onPendingKeysChange} 
                onBannerUpdate={onBannerUpdate}
                exportInfo={exportInfo}
              />
            </Table.Cell>
          </Table.Row>

          <Table.Row _style={{ paddingTop: '1.25rem' }}>
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Thumbprint</strong></Table.Cell>
            <Table.Cell _style={{ verticalAlign: 'top', paddingLeft: '2rem' }}>
              <KeyPair keyPairInfo={keyPairInfo} setKeyPairInfo={setKeyPairInfo} />
            </Table.Cell>
          </Table.Row>

          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Protocol Version</strong></Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', verticalAlign: 'top', paddingLeft: '2rem' }}>
              {keyPairInfo?.protocolVersion}
            </Table.Cell>
          </Table.Row>

          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong>Public Key</strong></Table.Cell>
            <Table.Cell _style={{ verticalAlign: 'top', paddingLeft: '2rem', wordBreak: 'break-word' }}>
              {keyPairInfo?.publicKey}
            </Table.Cell>
          </Table.Row>

          <Table.Row>
            <Table.Cell _style={{ verticalAlign: 'top' }}><strong></strong></Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'nowrap', verticalAlign: 'top', paddingLeft: '2rem' }}>
            <Button text="Manage Key Pair" onClick={onManageKeyPair} />
            </Table.Cell>
          </Table.Row>

          {internalSiteEnabled && (
            <>
              <Table.Row>
                <Table.Cell _style={{ verticalAlign: 'top' }}><strong></strong></Table.Cell>
                <Table.Cell _style={{ whiteSpace: 'nowrap', verticalAlign: 'top', paddingLeft: '2rem' }}>
                <Button text="Restore Keys from Backup" onClick={onRestoreKeys} />
                </Table.Cell>
              </Table.Row>
            </>
          )}  

          
        </Table.Body>
      </Table>
      </div>


    
    </>
    

  );
};

export default MainPage; 